/**
 * Sample Data Service
 *
 * @version 1.0
 * @updated 2026-02-10
 * @module core/modules/environmental/services
 *
 * @description
 * Servicio para cargar datos de ejemplo en el mapa para testing y demo.
 * Incluye capas de árboles, parques, hotspots e intervenciones.
 */

import { mapEngine } from '../../../map-engine/MapEngineService';
import { allSampleFeatures } from '../data/sample-features-sanjuan';

/**
 * Colores por tipo de feature (siguiendo paleta WESTEER)
 */
const LAYER_COLORS = {
  trees: '#10B981', // Green-500
  parks: '#34D399', // Green-400
  hotspots: {
    critical: '#EF4444', // Red-500
    high: '#F97316', // Orange-500
    medium: '#F59E0B', // Amber-500
    low: '#FCD34D', // Amber-300
  },
  interventions: {
    active: '#3B82F6', // Blue-500
    planned: '#8B5CF6', // Purple-500
    completed: '#10B981', // Green-500
  },
};

/**
 * Carga todas las capas de ejemplo en el mapa
 *
 * @remarks
 * Este método debe llamarse después de que el mapa esté cargado (evento 'load').
 *
 * @example
 * ```typescript
 * map.on('load', () => {
 *   SampleDataService.loadAllLayers();
 * });
 * ```
 */
export class SampleDataService {
  /**
   * Carga todas las capas de ejemplo
   */
  public static loadAllLayers(): void {
    console.log('[SampleDataService] Cargando capas de ejemplo...');

    try {
      this.loadTreesLayer();
      this.loadParksLayer();
      this.loadHotspotsLayer();
      this.loadInterventionsLayer();

      console.log('[SampleDataService] ✅ Todas las capas cargadas exitosamente');
    } catch (error) {
      console.error('[SampleDataService] ❌ Error cargando capas:', error);
    }
  }

  /**
   * Carga la capa de árboles
   */
  private static loadTreesLayer(): void {
    mapEngine.addLayer({
      id: 'env-trees',
      type: 'circle',
      source: {
        type: 'geojson',
        data: allSampleFeatures.trees,
      },
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          4, // zoom 10: 4px
          15,
          8, // zoom 15: 8px
          18,
          12, // zoom 18: 12px
        ],
        'circle-color': LAYER_COLORS.trees,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
        'circle-opacity': 0.85,
      },
    });

    console.log('[SampleDataService] ✓ Capa de árboles cargada (5 features)');
  }

  /**
   * Carga la capa de parques
   */
  private static loadParksLayer(): void {
    // Capa de relleno (fill)
    mapEngine.addLayer({
      id: 'env-parks-fill',
      type: 'fill',
      source: {
        type: 'geojson',
        data: allSampleFeatures.parks,
      },
      paint: {
        'fill-color': LAYER_COLORS.parks,
        'fill-opacity': 0.25,
      },
    });

    // Capa de borde (line)
    mapEngine.addLayer({
      id: 'env-parks-line',
      type: 'line',
      source: 'env-parks-fill-source', // Reusar source
      paint: {
        'line-color': LAYER_COLORS.parks,
        'line-width': 2,
        'line-opacity': 0.8,
      },
    });

    console.log('[SampleDataService] ✓ Capa de parques cargada (3 features)');
  }

  /**
   * Carga la capa de hotspots (zonas críticas)
   */
  private static loadHotspotsLayer(): void {
    // Capa de relleno con colores según severidad
    mapEngine.addLayer({
      id: 'env-hotspots-fill',
      type: 'fill',
      source: {
        type: 'geojson',
        data: allSampleFeatures.hotspots,
      },
      paint: {
        'fill-color': [
          'match',
          ['get', 'severity'],
          'critical',
          LAYER_COLORS.hotspots.critical,
          'high',
          LAYER_COLORS.hotspots.high,
          'medium',
          LAYER_COLORS.hotspots.medium,
          'low',
          LAYER_COLORS.hotspots.low,
          '#999999', // fallback
        ],
        'fill-opacity': 0.3,
      },
    });

    // Capa de borde
    mapEngine.addLayer({
      id: 'env-hotspots-line',
      type: 'line',
      source: 'env-hotspots-fill-source',
      paint: {
        'line-color': [
          'match',
          ['get', 'severity'],
          'critical',
          LAYER_COLORS.hotspots.critical,
          'high',
          LAYER_COLORS.hotspots.high,
          'medium',
          LAYER_COLORS.hotspots.medium,
          'low',
          LAYER_COLORS.hotspots.low,
          '#999999',
        ],
        'line-width': 2,
        'line-opacity': 0.8,
        'line-dasharray': [2, 2], // Línea punteada
      },
    });

    console.log('[SampleDataService] ✓ Capa de hotspots cargada (3 features)');
  }

  /**
   * Carga la capa de intervenciones
   */
  private static loadInterventionsLayer(): void {
    mapEngine.addLayer({
      id: 'env-interventions',
      type: 'circle',
      source: {
        type: 'geojson',
        data: allSampleFeatures.interventions,
      },
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          6,
          15,
          12,
          18,
          18,
        ],
        'circle-color': [
          'match',
          ['get', 'status'],
          'active',
          LAYER_COLORS.interventions.active,
          'planned',
          LAYER_COLORS.interventions.planned,
          'completed',
          LAYER_COLORS.interventions.completed,
          '#999999',
        ],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#FFFFFF',
        'circle-opacity': 0.9,
      },
    });

    console.log('[SampleDataService] ✓ Capa de intervenciones cargada (3 features)');
  }

  /**
   * Elimina todas las capas de ejemplo del mapa
   */
  public static removeAllLayers(): void {
    console.log('[SampleDataService] Eliminando capas de ejemplo...');

    const layerIds = [
      'env-trees',
      'env-parks-fill',
      'env-parks-line',
      'env-hotspots-fill',
      'env-hotspots-line',
      'env-interventions',
    ];

    layerIds.forEach((layerId) => {
      try {
        if (mapEngine.hasLayer(layerId)) {
          mapEngine.removeLayer(layerId);
        }
      } catch (error) {
        console.warn(`[SampleDataService] No se pudo eliminar capa ${layerId}:`, error);
      }
    });

    console.log('[SampleDataService] ✓ Capas eliminadas');
  }

  /**
   * Verifica si las capas de ejemplo están cargadas
   */
  public static areLayersLoaded(): boolean {
    return mapEngine.hasLayer('env-trees');
  }
}
